/**
 * Quixo Audit dashboard (customer, read-only). One page for the four things a customer asks about their audits:
 * how they are doing (scores and trend), what is running (operations), what they have left (included re-runs) and what
 * they paid (orders and spend). Nothing on this page changes data.
 *
 * Sources, all tenant-scoped on the server:
 *   GET /v1/growth-audit/jobs      audits, status, timings, included re-runs       (qm-audit via qm-api)
 *   GET /v1/growth-audit/history   finished audits with overall score and grade    (qm-audit via qm-api)
 *   GET /v1/growth-audit/compare   before/after for two audits of one website      (loaded only when opened)
 *   GET /v1/billing/history        payments, refunds and grants (Stripe ledger)    (owners and admins only)
 */
import { useState } from 'react';
import { Link } from 'react-router';
import { Badge, DataTable, EmptyState, ErrorState, Heading, Loading, PageHeader, Panel, Stat, Text, type Column } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { BffError } from '../../../lib/bff';
import { useFormat } from '../../../lib/format';
import { useResource, type ResourceState } from '../../../lib/resource';
import { useWorkspaceName } from '../_shared/ListScreen';
import { tierKey } from './audit-view';

/* ------------------------------------------------------------------ data shapes (what the API returns) */

export interface DashJob {
  id: string;
  status: 'in_progress' | 'delayed' | 'ready';
  tier: string;
  targetUrl: string;
  createdAt: string;
  artifactsReadyAt: string | null;
  deliveredAt: string | null;
  rerunOf?: string | null;
  includedReruns?: { included: number; used: number; remaining: number; canRerun: boolean; reason: string | null; until: string | null } | null;
}

export interface HistoryRow {
  audit_id: string;
  target_host: string;
  method_version: string;
  tier: string;
  overall: number | null;
  grade: string | null;
  completed_at: string;
  /** per-factor scores of that audit (absent on older API responses) */
  factors?: { key: string; label: string; score: number | null }[];
}

export interface LedgerItem { id: string; kind: 'payment' | 'refund' | 'grant'; at: string; amountMinor: number; currency: string | null; sku?: string }

interface Compare {
  comparable: boolean;
  warning?: string;
  overallDelta?: number | null;
  factorDeltas?: { key: string; label: string; before: number | null; after: number | null; delta: number | null }[];
  changedChecks: { checkId: string; title: string; area: string; before: { state: string }; after: { state: string }; direction: 'improved' | 'worse' | 'changed' }[];
  addedChecks: string[];
  removedChecks: string[];
}

/* ------------------------------------------------------------------ pure helpers (unit tested) */

/** Target of the pipeline: a finished report within 15 minutes of the order (NFR NC04-002). */
export const DELIVERY_TARGET_MINUTES = 15;

export function hostOf(url: string): string {
  try { return new URL(url).hostname.toLowerCase().replace(/^www\./, ''); } catch { return url; }
}

export function minutesTaken(j: Pick<DashJob, 'createdAt' | 'artifactsReadyAt' | 'deliveredAt'>): number | null {
  const end = j.artifactsReadyAt ?? j.deliveredAt;
  if (!end) return null;
  const ms = Date.parse(end) - Date.parse(j.createdAt);
  return Number.isFinite(ms) && ms >= 0 ? Math.round(ms / 60_000) : null;
}

export interface SiteTrend {
  host: string;
  audits: number;
  latest: HistoryRow;
  /** change against the previous audit of the same site and method version; null when there is none to compare */
  delta: number | null;
  /** oldest first, only audits that have a score */
  series: { at: string; overall: number }[];
  previousId: string | null;
}

export function siteTrends(rows: readonly HistoryRow[]): SiteTrend[] {
  const byHost = new Map<string, HistoryRow[]>();
  for (const r of rows) (byHost.get(r.target_host) ?? byHost.set(r.target_host, []).get(r.target_host)!).push(r);
  const out: SiteTrend[] = [];
  for (const [host, list] of byHost) {
    const asc = [...list].sort((a, b) => Date.parse(a.completed_at) - Date.parse(b.completed_at));
    const latest = asc[asc.length - 1]!;
    const previous = [...asc.slice(0, -1)].reverse().find((r) => r.method_version === latest.method_version && r.overall !== null) ?? null;
    out.push({
      host, audits: asc.length, latest,
      delta: previous && latest.overall !== null && previous.overall !== null ? latest.overall - previous.overall : null,
      series: asc.filter((r): r is HistoryRow & { overall: number } => r.overall !== null).map((r) => ({ at: r.completed_at, overall: r.overall })),
      previousId: previous?.audit_id ?? null,
    });
  }
  return out.sort((a, b) => Date.parse(b.latest.completed_at) - Date.parse(a.latest.completed_at));
}

export interface Ops { inProgress: number; delayed: number; ready: number; medianMinutes: number | null; withinTarget: number; measured: number }

export function opsSummary(jobs: readonly DashJob[]): Ops {
  const taken = jobs.map(minutesTaken).filter((m): m is number => m !== null).sort((a, b) => a - b);
  const mid = taken.length ? (taken.length % 2 ? taken[(taken.length - 1) / 2]! : Math.round((taken[taken.length / 2 - 1]! + taken[taken.length / 2]!) / 2)) : null;
  return {
    inProgress: jobs.filter((j) => j.status === 'in_progress').length, delayed: jobs.filter((j) => j.status === 'delayed').length, ready: jobs.filter((j) => j.status === 'ready').length,
    medianMinutes: mid, withinTarget: taken.filter((m) => m <= DELIVERY_TARGET_MINUTES).length, measured: taken.length,
  };
}

/** Free re-runs left across the original paid audits (re-runs themselves never carry an allowance of their own). */
export function rerunSummary(jobs: readonly DashJob[]) {
  const originals = jobs.filter((j) => !j.rerunOf && j.includedReruns);
  return {
    originals,
    left: originals.reduce((n, j) => n + (j.includedReruns?.remaining ?? 0), 0),
    used: originals.reduce((n, j) => n + (j.includedReruns?.used ?? 0), 0),
  };
}

/** Net paid per currency: payments minus refunds (grants are free and ignored). */
export function netSpend(items: readonly LedgerItem[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const i of items) {
    if (!i.currency || (i.kind !== 'payment' && i.kind !== 'refund')) continue;
    out[i.currency] = (out[i.currency] ?? 0) + (i.kind === 'payment' ? i.amountMinor : -Math.abs(i.amountMinor));
  }
  return out;
}

/* ------------------------------------------------------------------ small pieces */

/** Lavender line, one series. The table next to it carries every number, so the chart is decoration plus shape. */
export function Sparkline({ points, label }: { points: readonly number[]; label: string }) {
  if (!points.length) return null;
  const W = 120; const H = 32; const PAD = 4;
  const x = (i: number) => (points.length === 1 ? W / 2 : PAD + (i * (W - 2 * PAD)) / (points.length - 1));
  const y = (v: number) => H - PAD - (Math.max(0, Math.min(100, v)) * (H - 2 * PAD)) / 100;
  const last = points.length - 1;
  return (
    <svg className="qm-spark" width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label}>
      <line x1={PAD} x2={W - PAD} y1={H - PAD} y2={H - PAD} stroke="var(--q-line, #EEEEEE)" strokeWidth="1" />
      {points.length > 1 ? <polyline fill="none" stroke="var(--q-hue-deep, #9C83D8)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" points={points.map((v, i) => `${x(i)},${y(v)}`).join(' ')} /> : null}
      <circle cx={x(last)} cy={y(points[last]!)} r="4" fill="var(--q-hue-deep, #9C83D8)" stroke="var(--q-ground, #FFFFFF)" strokeWidth="2" />
    </svg>
  );
}

function Delta({ value }: { value: number | null }) {
  const { t } = useApp();
  const f = useFormat();
  if (value === null) return <Badge>{t('shell.screens.auditDash.noCompare')}</Badge>;
  if (value === 0) return <Badge>{t('shell.screens.auditDash.noChange')}</Badge>;
  return <Badge tone={value > 0 ? 'success' : 'warning'}>{value > 0 ? '▲ +' : '▼ '}{f.number(value)}</Badge>;
}

function SiteChanges({ baseId, currentId }: { baseId: string; currentId: string }) {
  const { t } = useApp();
  const f = useFormat();
  const res = useResource<Compare>(`/v1/growth-audit/compare?base=${encodeURIComponent(baseId)}&current=${encodeURIComponent(currentId)}`);
  if (res.status === 'loading') return <Loading label={t('common.loading')} />;
  if (res.status === 'error') return <ErrorState message={t('shell.error.load')} />;
  const d = res.data;
  const state = (s: string) => t(`shell.screens.auditDash.state.${s}`);
  return (
    <div className="qm-stack">
      {!d.comparable ? <Text tone="muted">{t('shell.screens.auditDash.methodChanged')}</Text> : null}
      {d.factorDeltas?.length ? (
        <DataTable caption={t('shell.screens.auditDash.factors')} columns={[
          { key: 'label', header: t('shell.screens.auditDash.factor'), render: (r) => <bdi>{f.text(r.label)}</bdi> },
          { key: 'before', header: t('shell.screens.auditDash.before'), numeric: true, render: (r) => (r.before === null ? t('shell.screens.auditDash.notMeasured') : f.number(r.before)) },
          { key: 'after', header: t('shell.screens.auditDash.after'), numeric: true, render: (r) => (r.after === null ? t('shell.screens.auditDash.notMeasured') : f.number(r.after)) },
          { key: 'delta', header: t('shell.screens.auditDash.change'), render: (r) => <Delta value={r.delta} /> },
        ] satisfies Column<{ id: string; label: string; before: number | null; after: number | null; delta: number | null }>[]}
        rows={d.factorDeltas.map((x) => ({ ...x, id: x.key }))} />
      ) : null}
      {d.changedChecks.length ? (
        <DataTable caption={t('shell.screens.auditDash.changedChecks')} columns={[
          { key: 'title', header: t('shell.screens.auditDash.check'), render: (r) => <bdi>{f.text(r.title)}</bdi> },
          { key: 'before', header: t('shell.screens.auditDash.before'), render: (r) => state(r.before.state) },
          { key: 'after', header: t('shell.screens.auditDash.after'), render: (r) => state(r.after.state) },
          { key: 'direction', header: t('shell.screens.auditDash.change'), render: (r) => <Badge tone={r.direction === 'improved' ? 'success' : r.direction === 'worse' ? 'warning' : 'neutral'}>{t(`shell.screens.auditDash.dir.${r.direction}`)}</Badge> },
        ] satisfies Column<Compare['changedChecks'][number] & { id: string }>[]}
        rows={d.changedChecks.map((x) => ({ ...x, id: x.checkId }))} />
      ) : <Text tone="muted">{t('shell.screens.auditDash.nothingChanged')}</Text>}
    </div>
  );
}

/* ------------------------------------------------------------------ the page */

export function GrowthAuditDashboard() {
  const jobs = useResource<DashJob[]>('/v1/growth-audit/jobs');
  const history = useResource<HistoryRow[]>('/v1/growth-audit/history?limit=100');
  const ledger = useResource<LedgerItem[]>('/v1/billing/history');
  return <DashboardView jobs={jobs} history={history} ledger={ledger} />;
}

/** The page body. Takes already-loaded resources so every state can be rendered and tested without a network. */
export function DashboardView({ jobs, history, ledger }: { jobs: ResourceState<DashJob[]>; history: ResourceState<HistoryRow[]>; ledger: ResourceState<LedgerItem[]> }) {
  const { t } = useApp();
  const f = useFormat();
  const workspace = useWorkspaceName();
  const [open, setOpen] = useState<string | null>(null);

  const jobList = jobs.status === 'ready' ? jobs.data : null;
  const sites = history.status === 'ready' ? siteTrends(history.data) : null;
  const ops = jobList ? opsSummary(jobList) : null;
  const reruns = jobList ? rerunSummary(jobList) : null;
  const spend = ledger.status === 'ready' ? netSpend(ledger.data) : null;
  const latest = sites?.[0] ?? null;
  const grade = (g: string | null) => (g ? t(`shell.screens.auditDash.grade.${g}`) : '');
  // billing "pro" and pipeline "professional" are one tier (G18)
  const tier = (v: string) => { const k = tierKey(v); return k ? t(`shell.screens.auditDash.tier.${k}`) : v; };
  const money = Object.entries(spend ?? {}).filter(([, v]) => v !== 0).map(([cur, v]) => f.money(v, cur)).join(' · ');

  return (
    <section className="qm-page" aria-labelledby="page-title">
      <PageHeader eyebrow={workspace} title={t('shell.screens.auditDash.title')} intro={t('shell.screens.auditDash.intro')}
        actions={<Link className="qm-text-link" to="/audit">{t('shell.screens.auditDash.reports')}</Link>} />

      <div className="qm-grid">
        <Panel>
          <Stat label={t('shell.screens.auditDash.latestScore')} value={latest?.latest.overall != null ? f.number(latest.latest.overall) : '-'} />
          {latest?.latest.grade ? <Text tone="muted">{grade(latest.latest.grade)} · <bdi>{f.text(latest.host)}</bdi></Text> : null}
        </Panel>
        <Panel><Stat label={t('shell.screens.auditDash.finished')} value={history.status === 'ready' ? f.number(history.data.length) : '-'} /></Panel>
        <Panel>
          <Stat label={t('shell.screens.auditDash.rerunsLeft')} value={reruns ? f.number(reruns.left) : '-'} />
          <Text tone="muted">{t('shell.screens.auditDash.rerunsHint')}</Text>
        </Panel>
        <Panel>
          <Stat label={t('shell.screens.auditDash.spend')} value={spend ? (money || '-') : '-'} />
          <Text tone="muted">{t('shell.screens.auditDash.spendHint')}</Text>
        </Panel>
      </div>

      <Panel>
        <Heading level={2}>{t('shell.screens.auditDash.trendsTitle')}</Heading>
        {history.status === 'loading' ? <Loading label={t('common.loading')} /> : history.status === 'error' ? <ErrorState message={t('shell.error.load')} /> : !sites?.length ? <EmptyState title={t('shell.screens.auditDash.noAudits')} /> : (
          <DataTable caption={t('shell.screens.auditDash.trendsTitle')} columns={[
            { key: 'host', header: t('shell.screens.auditDash.site'), render: (r) => <bdi>{f.text(r.host)}</bdi> },
            { key: 'score', header: t('shell.screens.auditDash.score'), numeric: true, render: (r) => (r.latest.overall === null ? t('shell.screens.auditDash.notMeasured') : [f.number(r.latest.overall), r.latest.grade ? grade(r.latest.grade) : ''].filter(Boolean).join(' · ')) },
            { key: 'delta', header: t('shell.screens.auditDash.change'), render: (r) => <Delta value={r.delta} /> },
            { key: 'trend', header: t('shell.screens.auditDash.trend'), render: (r) => <Sparkline points={r.series.map((p) => p.overall)} label={t('shell.screens.auditDash.trendLabel', { host: r.host, first: r.series[0]?.overall ?? 0, last: r.series[r.series.length - 1]?.overall ?? 0 })} /> },
            { key: 'audits', header: t('shell.screens.auditDash.audits'), numeric: true, render: (r) => f.number(r.audits) },
            { key: 'last', header: t('shell.screens.auditDash.lastAudit'), render: (r) => f.date(r.latest.completed_at) },
            { key: 'more', header: t('shell.screens.auditDash.details'), render: (r) => (r.previousId ? (
              <button type="button" className="qm-link-button" aria-expanded={open === r.host} onClick={() => setOpen(open === r.host ? null : r.host)}>
                {open === r.host ? t('shell.screens.auditDash.hide') : t('shell.screens.auditDash.whatChanged')}
              </button>
            ) : '') },
          ] satisfies Column<SiteTrend & { id: string }>[]} rows={sites.map((s) => ({ ...s, id: s.host }))} />
        )}
        {sites?.filter((s) => open === s.host && s.previousId).map((s) => (
          <div key={s.host}>
            <Heading level={3}><bdi>{f.text(s.host)}</bdi></Heading>
            <SiteChanges baseId={s.previousId!} currentId={s.latest.audit_id} />
          </div>
        ))}
      </Panel>

      <Panel>
        <Heading level={2}>{t('shell.screens.auditDash.opsTitle')}</Heading>
        {jobs.status === 'loading' ? <Loading label={t('common.loading')} /> : jobs.status === 'error' ? <ErrorState message={t('shell.error.load')} /> : !jobList?.length ? <EmptyState title={t('shell.screens.auditDash.noJobs')} /> : (
          <>
            <div className="qm-grid">
              <Stat label={t('shell.screens.auditDash.inProgress')} value={f.number(ops!.inProgress)} />
              <Stat label={t('shell.screens.auditDash.delayed')} value={f.number(ops!.delayed)} />
              <Stat label={t('shell.screens.auditDash.ready')} value={f.number(ops!.ready)} />
              <Stat label={t('shell.screens.auditDash.median')} value={ops!.medianMinutes === null ? '-' : f.number(ops!.medianMinutes)} />
            </div>
            {ops!.measured ? <Text tone="muted">{t('shell.screens.auditDash.target', { within: f.number(ops!.withinTarget), total: f.number(ops!.measured), minutes: DELIVERY_TARGET_MINUTES })}</Text> : null}
            {ops!.delayed ? <Text role="status">{t('shell.screens.auditDash.delayedHelp')}</Text> : null}
            <DataTable caption={t('shell.screens.auditDash.opsTitle')} columns={[
              { key: 'site', header: t('shell.screens.auditDash.site'), render: (r) => <Link to={`/audit/${encodeURIComponent(r.id)}`}><bdi>{f.text(hostOf(r.targetUrl))}</bdi></Link> },
              { key: 'tier', header: t('shell.screens.auditDash.tierCol'), render: (r) => tier(r.tier) },
              { key: 'status', header: t('shell.screens.auditDash.status'), render: (r) => <Badge tone={r.status === 'ready' ? 'success' : r.status === 'delayed' ? 'warning' : 'accent'}>{t(`shell.screens.auditDash.status.${r.status}`)}</Badge> },
              { key: 'kind', header: t('shell.screens.auditDash.kind'), render: (r) => (r.rerunOf ? t('shell.screens.auditDash.kindRerun') : t('shell.screens.auditDash.kindAudit')) },
              { key: 'started', header: t('shell.screens.auditDash.started'), render: (r) => f.dateTime(r.createdAt) },
              { key: 'took', header: t('shell.screens.auditDash.took'), numeric: true, render: (r) => { const m = minutesTaken(r); return m === null ? '' : f.number(m); } },
            ] satisfies Column<DashJob>[]} rows={jobList} />
          </>
        )}
      </Panel>

      <Panel>
        <Heading level={2}>{t('shell.screens.auditDash.rerunsTitle')}</Heading>
        {jobs.status === 'loading' ? <Loading label={t('common.loading')} /> : !reruns?.originals.length ? <Text tone="muted">{t('shell.screens.auditDash.rerunsNone')}</Text> : (
          <DataTable caption={t('shell.screens.auditDash.rerunsTitle')} columns={[
            { key: 'site', header: t('shell.screens.auditDash.site'), render: (r) => <bdi>{f.text(hostOf(r.targetUrl))}</bdi> },
            { key: 'tier', header: t('shell.screens.auditDash.tierCol'), render: (r) => tier(r.tier) },
            { key: 'used', header: t('shell.screens.auditDash.used'), numeric: true, render: (r) => `${f.number(r.includedReruns!.used)} / ${f.number(r.includedReruns!.included)}` },
            { key: 'left', header: t('shell.screens.auditDash.left'), numeric: true, render: (r) => f.number(r.includedReruns!.remaining) },
            { key: 'until', header: t('shell.screens.auditDash.until'), render: (r) => f.date(r.includedReruns!.until) },
          ] satisfies Column<DashJob>[]} rows={[...reruns.originals]} />
        )}
      </Panel>

      <Panel>
        <Heading level={2}>{t('shell.screens.auditDash.ordersTitle')}</Heading>
        {ledger.status === 'loading' ? <Loading label={t('common.loading')} /> : ledger.status === 'error'
          ? (ledger.error instanceof BffError && ledger.error.status === 403 ? <Text tone="muted">{t('shell.screens.auditDash.ordersRole')}</Text> : <ErrorState message={t('shell.error.load')} />)
          : !ledger.data.length ? <EmptyState title={t('shell.screens.auditDash.noOrders')} /> : (
            <DataTable caption={t('shell.screens.auditDash.ordersTitle')} columns={[
              { key: 'at', header: t('shell.screens.auditDash.date'), render: (r) => f.date(r.at) },
              { key: 'kind', header: t('shell.screens.auditDash.type'), render: (r) => t(`shell.screens.auditDash.ledger.${r.kind}`) },
              { key: 'amount', header: t('shell.screens.auditDash.amount'), numeric: true, render: (r) => (r.kind === 'grant' || !r.currency ? '' : f.money(r.kind === 'refund' ? -Math.abs(r.amountMinor) : r.amountMinor, r.currency)) },
            ] satisfies Column<LedgerItem>[]} rows={ledger.data} />
          )}
      </Panel>
    </section>
  );
}
